import { z } from 'zod';
import { notificationSchema } from './dto/notification.js';
/** Queue names. One queue per job kind, bound to the `refuelled.jobs` exchange. */
export const JOB_KINDS = [
    'plan.generate',
    'plan.swap',
    // One queue for every connected provider; the payload names which (ADR-019).
    'sync.provider',
    // Kept one release so jobs queued before the change still drain.
    'sync.strava',
    'sync.garmin',
    'sync.health',
    'import.recipe_url',
    // A file somebody uploaded, of any format the importer knows (ADR-020).
    'import.file',
    'import.label_ocr',
    'import.product',
    'import.reference',
    // A restaurant's published menu, read for an admin to approve (ADR-029).
    'import.restaurant',
    'account.export',
];
export const jobStatusSchema = z.enum(['queued', 'running', 'done', 'failed', 'cancelled']);
/** Envelope every published job carries. */
export const jobEnvelopeSchema = z.object({
    jobId: z.string().uuid(),
    tenantId: z.string().uuid(),
    kind: z.enum(JOB_KINDS),
    payload: z.record(z.unknown()),
    attempt: z.number().int().min(1).default(1),
    publishedAt: z.string(),
});
/** Progress events relayed to the tenant's socket.io room. */
export const jobProgressSchema = z.object({
    jobId: z.string().uuid(),
    tenantId: z.string().uuid(),
    kind: z.enum(JOB_KINDS),
    status: jobStatusSchema,
    progress: z.number().int().min(0).max(100),
    stage: z.string().optional(),
    result: z.record(z.unknown()).optional(),
    error: z.string().optional(),
});
/**
 * A notification, on its way to the one socket that should see it. It carries
 * the recipient's login rather than their member id because that is what the
 * socket room is keyed by — the alternative is a database lookup on every
 * handshake to learn something the sender already knew.
 */
export const notificationEventSchema = z.object({
    type: z.literal('notification.created'),
    tenantId: z.string().uuid(),
    userId: z.string().uuid(),
    notification: notificationSchema,
});
export function isNotificationEvent(event) {
    return 'type' in event && event.type === 'notification.created';
}
/**
 * Every exchange and queue name is namespaced.
 *
 * Without this, `pnpm test` and `pnpm dev` fight: the running agents consume
 * the messages a test published, and the test times out waiting for work that
 * another process already did. Tests set their own prefix; everything else
 * uses the default.
 */
export function brokerNames(prefix = '') {
    const p = prefix ? `${prefix}.` : '';
    return {
        jobs: `${p}refuelled.jobs`,
        events: `${p}refuelled.events`,
        dlx: `${p}refuelled.dlx`,
        queueFor: (kind) => `${p}q.${kind}`,
    };
}
export const JOBS_EXCHANGE = brokerNames().jobs;
export const EVENTS_EXCHANGE = brokerNames().events;
export const DLX_EXCHANGE = brokerNames().dlx;
export function queueNameFor(kind, prefix = '') {
    return brokerNames(prefix).queueFor(kind);
}
//# sourceMappingURL=jobs.js.map